import BenchBoostImage from '@components/chip-stratgey-banner/images/bench-boost.svg';
import FreeHitImage from '@components/chip-stratgey-banner/images/free-hit.svg';
import TripleCaptainImage from '@components/chip-stratgey-banner/images/triple-captain.svg';
import WildcardImage from '@components/chip-stratgey-banner/images/wildcard.svg';
import type { ChipType, PlannerChip } from '@api-service/planner';

// FPL splits the season into two halves at GW19: each chip has one use per
// half, so the second use only unlocks once GW19 is locked (i.e. from GW20).
export const HALF_SEASON_SPLIT_GAMEWEEK = 19;

const FIRST_HALF = 1;
const SECOND_HALF = 2;

// Free hit and wildcard grant extra transfers, and transfers do not exist
// before the season starts, so FPL never allows either chip in gameweek 1.
const FIRST_GAMEWEEK = 1;
const CHIPS_UNAVAILABLE_IN_FIRST_GAMEWEEK: ReadonlySet<ChipType> = new Set([
  'FREE_HIT',
  'WILDCARD'
]);

export const isChipBlockedInGameweek = (
  chipType: ChipType,
  gameweek: number
): boolean =>
  gameweek === FIRST_GAMEWEEK &&
  CHIPS_UNAVAILABLE_IN_FIRST_GAMEWEEK.has(chipType);

export type ChipSelectionState = {
  playedGameweeks: number[];
  selectedGameweek: number | null;
  canActivate: boolean;
};

export type ChipSelections = Record<ChipType, ChipSelectionState>;

export const CHIP_ORDER: ChipType[] = [
  'BENCH_BOOST',
  'FREE_HIT',
  'WILDCARD',
  'TRIPLE_CAPTAIN'
];

export type ChipContent = {
  label: string;
  description: string;
  guideUrl: string;
};

export const CHIP_CONTENT: Record<ChipType, ChipContent> = {
  BENCH_BOOST: {
    label: 'Bench boost',
    description:
      'Includes the points scored from your benched players in your total points for a gameweek.',
    guideUrl: '/what-is-the-fpl-bench-boost-and-when-to-use-it'
  },
  FREE_HIT: {
    label: 'Free hit',
    description:
      'Make unlimited transfers for a single gameweek. Your team reverts at the next deadline.',
    guideUrl: '/fpl-free-hit-chip'
  },
  WILDCARD: {
    label: 'Wildcard',
    description:
      'Make unlimited free transfers until the next gameweek deadline.',
    guideUrl: '/fpl-wildcard'
  },
  TRIPLE_CAPTAIN: {
    label: 'Triple captain',
    description: "Your captain's points are tripled instead of doubled.",
    guideUrl: '/fpl-chip-strategy-guide'
  }
};

export const CHIP_IMAGES: Record<ChipType, string> = {
  BENCH_BOOST: BenchBoostImage,
  FREE_HIT: FreeHitImage,
  WILDCARD: WildcardImage,
  TRIPLE_CAPTAIN: TripleCaptainImage
};

/**
 * `PlannerGameweek.activeChip` is a loose string in the generated schema, not
 * a `ChipType` union, so values outside the four known chips (e.g. a future
 * `MANAGER`) can arrive from the API. Resolve to the friendly label where we
 * have one, otherwise humanise the enum instead of leaking it to the screen.
 */
export const resolveChipLabel = (activeChip: string): string => {
  const content = CHIP_CONTENT[activeChip as ChipType];
  if (content) return content.label;
  const humanised = activeChip.toLowerCase().split('_').join(' ');
  return humanised.charAt(0).toUpperCase() + humanised.slice(1);
};

/** Null for unknown chips; callers render without the icon in that case. */
export const resolveChipImage = (activeChip: string): string | null =>
  CHIP_IMAGES[activeChip as ChipType] ?? null;

const createEmptyChipState = (): ChipSelectionState => ({
  playedGameweeks: [],
  selectedGameweek: null,
  canActivate: true
});

const createEmptyChipSelections = (): ChipSelections =>
  Object.fromEntries(
    CHIP_ORDER.map((chipType) => [chipType, createEmptyChipState()])
  ) as ChipSelections;

/**
 * Derives the per-chip selection state from the planner's chip usages.
 * Mirrors scoreline-app's buildChipSelections (planner-transforms.ts): a chip
 * can be (re)scheduled only while its current-half usage is unset or still in
 * the future; a usage on or before the locked gameweek cannot be moved.
 */
export const buildChipSelections = (
  chips: PlannerChip[],
  lockedThroughGameweek: number
): ChipSelections => {
  const currentHalf =
    lockedThroughGameweek >= HALF_SEASON_SPLIT_GAMEWEEK
      ? SECOND_HALF
      : FIRST_HALF;

  return chips.reduce<ChipSelections>((selections, chip) => {
    const chipType = chip.type as ChipType;
    if (!(chipType in selections)) return selections;

    const playedGameweeks = chip.usages
      .map((usage) => usage.gameweek)
      .filter(
        (gameweek): gameweek is number =>
          gameweek != null && gameweek <= lockedThroughGameweek
      );

    // `half` is optional in the generated type; fall back to usage order.
    const currentHalfUsage =
      chip.usages.find((usage) => usage.half === currentHalf) ??
      chip.usages[currentHalf - 1];

    let canActivate: boolean;
    let selectedGameweek: number | null;

    if (currentHalfUsage == null || currentHalfUsage.gameweek == null) {
      canActivate = true;
      selectedGameweek = null;
    } else if (currentHalfUsage.gameweek > lockedThroughGameweek) {
      canActivate = true;
      selectedGameweek = currentHalfUsage.gameweek;
    } else {
      canActivate = false;
      selectedGameweek = null;
    }

    return {
      ...selections,
      [chipType]: { playedGameweeks, selectedGameweek, canActivate }
    };
  }, createEmptyChipSelections());
};

export type GameweekOptionsForChip = {
  chipType: ChipType;
  selections: ChipSelections;
  availableGameweeks: number[];
  lockedThroughGameweek: number;
};

/**
 * The gameweeks a chip may be scheduled in: future gameweeks only, within the
 * current half of the season, excluding gameweeks already played or selected
 * by any chip. The chip's own current selection is offered so it stays
 * visible in the dropdown, with one exception: free hit and wildcard can
 * never be offered gameweek 1 (FPL forbids it), even if a stale selection
 * exists there. Such a stale selection also does not block gameweek 1 for
 * the other chips.
 */
export const buildGameweekOptionsForChip = ({
  chipType,
  selections,
  availableGameweeks,
  lockedThroughGameweek
}: GameweekOptionsForChip): number[] => {
  const isFirstHalf = lockedThroughGameweek < HALF_SEASON_SPLIT_GAMEWEEK;
  const ownSelectedGameweek = selections[chipType].selectedGameweek;

  const usedGameweeks = new Set(
    Object.entries(selections).flatMap(([selectionChipType, state]) => {
      const { selectedGameweek } = state;
      const hasValidSelection =
        selectedGameweek !== null &&
        !isChipBlockedInGameweek(
          selectionChipType as ChipType,
          selectedGameweek
        );

      return [
        ...state.playedGameweeks,
        ...(hasValidSelection ? [selectedGameweek] : [])
      ];
    })
  );

  return availableGameweeks.filter((gameweek) => {
    if (isChipBlockedInGameweek(chipType, gameweek)) return false;
    if (gameweek === ownSelectedGameweek) return true;
    if (gameweek <= lockedThroughGameweek) return false;
    if (isFirstHalf && gameweek > HALF_SEASON_SPLIT_GAMEWEEK) return false;
    return !usedGameweeks.has(gameweek);
  });
};

/**
 * What a chip can do in the gameweek being viewed in the planner, which is
 * where FPL itself plays chips: one gameweek at a time, one chip per week.
 *
 * - `active`: the chip is played in this gameweek.
 * - `available`: nothing stops the chip being played here.
 * - `plannedElsewhere`: the chip is planned for another future gameweek;
 *   playing it here moves it (the backend supersedes the earlier activation).
 * - `replaces`: another chip is played in this gameweek. The planner would
 *   store both, which FPL forbids, so playing this one must clear that one.
 * - `played`: the chip has been used in this half of the season.
 * - `blocked`: an FPL rule rules this gameweek out for the chip.
 */
export type GameweekChipState =
  | { kind: 'active' }
  | { kind: 'available' }
  | { kind: 'plannedElsewhere'; plannedGameweek: number }
  | { kind: 'replaces'; activeChip: ChipType }
  | { kind: 'played'; playedGameweek: number | null }
  | { kind: 'blocked'; reason: 'FIRST_GAMEWEEK' | 'NEXT_HALF' };

export type GameweekChipStateInput = {
  chipType: ChipType;
  gameweek: number;
  selections: ChipSelections;
  lockedThroughGameweek: number;
};

const findChipPlayedIn = (
  selections: ChipSelections,
  gameweek: number
): ChipType | null =>
  CHIP_ORDER.find(
    (chipType) => selections[chipType].selectedGameweek === gameweek
  ) ?? null;

/**
 * Resolves a chip's state for a future gameweek. Callers only ask about
 * gameweeks after `lockedThroughGameweek`: a gameweek that has started can no
 * longer have its chip changed.
 */
export const deriveGameweekChipState = ({
  chipType,
  gameweek,
  selections,
  lockedThroughGameweek
}: GameweekChipStateInput): GameweekChipState => {
  const selection = selections[chipType];

  if (selection.selectedGameweek === gameweek) return { kind: 'active' };

  if (!selection.canActivate) {
    const { playedGameweeks } = selection;
    return {
      kind: 'played',
      playedGameweek:
        playedGameweeks.length > 0 ? Math.max(...playedGameweeks) : null
    };
  }

  if (isChipBlockedInGameweek(chipType, gameweek)) {
    return { kind: 'blocked', reason: 'FIRST_GAMEWEEK' };
  }

  const isFirstHalf = lockedThroughGameweek < HALF_SEASON_SPLIT_GAMEWEEK;
  if (isFirstHalf && gameweek > HALF_SEASON_SPLIT_GAMEWEEK) {
    return { kind: 'blocked', reason: 'NEXT_HALF' };
  }

  const activeChip = findChipPlayedIn(selections, gameweek);
  if (activeChip) return { kind: 'replaces', activeChip };

  if (selection.selectedGameweek !== null) {
    return {
      kind: 'plannedElsewhere',
      plannedGameweek: selection.selectedGameweek
    };
  }

  return { kind: 'available' };
};
